<script setup lang="ts">
import { computed, ref } from "vue";
import { renderMarkdownInline } from "@utils/renderMarkdown";
import type { RadarRow, RadarSeries } from "@/composables/useRadarChartData";

const props = defineProps({
  axes: { type: Array as () => string[], required: true },
  series: { type: Array as () => RadarSeries[], required: true },
  min: { type: Number, default: 0 },
  max: { type: Number, default: 10 },
});

const WIDTH = 580;
const HEIGHT = 430;
const CX = WIDTH / 2;
const CY = HEIGHT / 2;
const RADIUS = HEIGHT / 2 - 68;
const RINGS = 4;

const angleFor = (idx: number) => (Math.PI * 2 * idx) / props.axes.length - Math.PI / 2;

function pointFor(idx: number, ratio: number): { x: number; y: number } {
  const angle = angleFor(idx);
  return { x: CX + Math.cos(angle) * RADIUS * ratio, y: CY + Math.sin(angle) * RADIUS * ratio };
}

const ringPolygons = computed(() =>
  Array.from({ length: RINGS }, (_, ring) => {
    const ratio = (ring + 1) / RINGS;
    return props.axes.map((_, idx) => {
      const { x, y } = pointFor(idx, ratio);
      return `${x},${y}`;
    }).join(" ");
  }),
);

const spokes = computed(() => props.axes.map((_, idx) => pointFor(idx, 1)));

const LABEL_LINE_LENGTH = 20;
const LABEL_MAX_LINES = 3;
const LABEL_LINE_HEIGHT = 12;

function wrapLabel(label: string): string[] {
  const lines: string[] = [];
  let current = "";
  for (const word of label.split(/\s+/)) {
    if (current && `${current} ${word}`.length > LABEL_LINE_LENGTH) {
      lines.push(current);
      current = word;
    } else {
      current = current ? `${current} ${word}` : word;
    }
  }
  if (current) lines.push(current);
  if (lines.length > LABEL_MAX_LINES) {
    lines.length = LABEL_MAX_LINES;
    lines[LABEL_MAX_LINES - 1] = `${lines[LABEL_MAX_LINES - 1].slice(0, LABEL_LINE_LENGTH - 1)}…`;
  }
  return lines;
}

const axisLabels = computed(() =>
  props.axes.map((label, idx) => {
    const { x, y } = pointFor(idx, 1.12);
    const cos = Math.cos(angleFor(idx));
    const sin = Math.sin(angleFor(idx));
    let anchor = "middle";
    if (cos > 0.25) anchor = "start";
    else if (cos < -0.25) anchor = "end";
    const lines = wrapLabel(label);
    const blockHeight = (lines.length - 1) * LABEL_LINE_HEIGHT;
    return { label, lines, x, y: y - blockHeight / 2 + (sin * blockHeight) / 2, anchor };
  }),
);

const ratioFor = (value: number) => {
  const range = props.max - props.min;
  if (range <= 0) return 0;
  return Math.min(1, Math.max(0, (value - props.min) / range));
};

const seriesShapes = computed(() =>
  props.series.map((serie) => {
    const points = serie.values.map((value, idx) =>
      value == null ? null : { ...pointFor(idx, ratioFor(value)), value },
    );
    const polygon = points.map((point) => (point ? `${point.x},${point.y}` : `${CX},${CY}`)).join(" ");
    return { ...serie, points, polygon };
  }),
);

const formatMean = (value: number) => value.toFixed(2);
const formatCount = (value: number) => Math.round(value);
const formatShare = (share: number) => `${Math.round(share * 100)}%`;
const isLikertRow = (row: RadarRow) => row.label !== row.code;

const HIT_RADIUS = 11;

const hovered = ref<{ serie: number; item: number } | null>(null);

const hoveredVertex = computed(() => {
  if (!hovered.value) return null;
  const serie = seriesShapes.value[hovered.value.serie];
  const point = serie?.points[hovered.value.item];
  if (!serie || !point) return null;
  const below = point.y < HEIGHT * 0.4;
  return {
    serie,
    item: hovered.value.item,
    stats: serie.details[hovered.value.item] ?? null,
    value: point.value,
    style: {
      left: `${(point.x / WIDTH) * 100}%`,
      top: `${(point.y / HEIGHT) * 100}%`,
      transform: below ? "translate(-50%, 14px)" : "translate(-50%, calc(-100% - 14px))",
    },
  };
});
</script>

<template>
  <div class="flex flex-col items-center gap-2">
    <div class="relative w-full max-w-xl">
      <svg
        :viewBox="`0 0 ${WIDTH} ${HEIGHT}`"
        class="w-full">
        <polygon
          v-for="(ring, idx) in ringPolygons"
          :key="`ring-${idx}`"
          :points="ring"
          fill="none"
          class="stroke-border"
          stroke-width="1" />
        <line
          v-for="(spoke, idx) in spokes"
          :key="`spoke-${idx}`"
          :x1="CX"
          :y1="CY"
          :x2="spoke.x"
          :y2="spoke.y"
          class="stroke-border"
          stroke-width="1" />

        <text
          v-for="(axis, idx) in axisLabels"
          :key="`label-${idx}`"
          :x="axis.x"
          :y="axis.y"
          :text-anchor="axis.anchor"
          dominant-baseline="middle"
          class="fill-muted-foreground text-2xs">
          <title>{{ axis.label }}</title>
          <tspan
            v-for="(line, lineIdx) in axis.lines"
            :key="lineIdx"
            :x="axis.x"
            :dy="lineIdx === 0 ? 0 : LABEL_LINE_HEIGHT">
            {{ line }}
          </tspan>
        </text>

        <g
          v-for="(serie, serieIdx) in seriesShapes"
          :key="serie.label">
          <polygon
            :points="serie.polygon"
            :fill="serie.color"
            fill-opacity="0.12"
            :stroke="serie.color"
            stroke-width="2"
            stroke-linejoin="round" />
          <template
            v-for="(point, idx) in serie.points"
            :key="`dot-${serie.label}-${idx}`">
            <circle
              v-if="point"
              :cx="point.x"
              :cy="point.y"
              :r="hovered?.serie === serieIdx && hovered?.item === idx ? 5 : 3.5"
              :fill="serie.color"
              pointer-events="none" />
            <circle
              v-if="point"
              :cx="point.x"
              :cy="point.y"
              :r="HIT_RADIUS"
              fill="transparent"
              class="cursor-default"
              @mouseenter="hovered = { serie: serieIdx, item: idx }"
              @mouseleave="hovered = null" />
          </template>
        </g>
      </svg>

      <div
        v-if="hoveredVertex"
        class="pointer-events-none absolute z-20 w-max max-w-[260px] rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground shadow-md"
        :style="hoveredVertex.style">
        <div class="flex flex-col gap-1">
          <span class="font-semibold" v-html="renderMarkdownInline(axes[hoveredVertex.item])" />
          <span
            v-if="series.length > 1"
            class="flex items-center gap-1.5">
            <span
              class="inline-block h-2 w-2 rounded-full"
              :style="{ backgroundColor: hoveredVertex.serie.color }" />
            {{ hoveredVertex.serie.label }}
          </span>
          <span class="tabular-nums">
            Mean <strong>{{ formatMean(hoveredVertex.value) }}</strong>
            <template v-if="hoveredVertex.stats"> · n={{ formatCount(hoveredVertex.stats.n) }}</template>
            <template v-if="hoveredVertex.stats?.skipped"> · {{ formatCount(hoveredVertex.stats.skipped) }} skipped</template>
          </span>
          <div
            v-if="hoveredVertex.stats?.rows.length"
            class="mt-0.5 grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-3 gap-y-0.5 border-t border-primary-foreground/20 pt-1 tabular-nums">
            <template
              v-for="row in hoveredVertex.stats.rows"
              :key="row.code">
              <span class="truncate">
                <span v-html="renderMarkdownInline(row.label)" />
                <span
                  v-if="isLikertRow(row)"
                  class="opacity-70"> = {{ row.value }}</span>
              </span>
              <span class="text-right">× {{ formatCount(row.count) }}</span>
              <span class="text-right opacity-70">{{ formatShare(row.share) }}</span>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span
        v-for="serie in series"
        :key="`legend-${serie.label}`"
        class="flex items-center gap-1.5">
        <span
          class="inline-block h-2.5 w-2.5 rounded-full"
          :style="{ backgroundColor: serie.color }" />
        {{ serie.label }}
      </span>
      <span class="text-3xs">scale {{ min }}–{{ max }}</span>
    </div>
  </div>
</template>
